<template>
  <div id="app">
    <Nav />
    <router-view v-if="isRegisterPage && !$auth.loading" />
    <div v-else-if="visibleAuthError && !$auth.loading && !isAuthInitializing" class="auth-error" role="alert">
      <AccountMigrationNotice v-if="visibleAuthError.action === 'migrate-account'" :required="true" />
      <p v-else>{{ visibleAuthError.message }}</p>
      <button v-if="visibleAuthError.action === 'migrate-account'" @click="signInWithGoogle">I’ve migrated — sign in again</button>
      <button v-else-if="visibleAuthError.action === 'google-login'" @click="signInWithGoogle">Sign in with Google</button>
      <button v-else-if="visibleAuthError.action === 'retry'" @click="retryAuth">Try Again</button>
      <button v-else @click="signInWithGoogle">Sign in again after resolving this issue</button>
    </div>
    <Loading
      id="app-loading"
      v-else-if="showLoading"
      :text="loadingText" />
    <router-view v-else-if="isReady" />
  </div>
</template>

<script>
import Nav from '@/components/nav/Nav'
import { mapGetters, mapState } from 'vuex'
import Loading from '@/components/misc/Loading'
import { initializeAuth } from '@/utils/authInitializer'
import Vue from 'vue'
import { bus } from '@/utils/event-bus'
import AccountMigrationNotice from '@/components/auth/AccountMigrationNotice'

export default {
  data () {
    return { dataLoadingClient: null }
  },
  components: {
    Nav,
    Loading,
    AccountMigrationNotice
  },
  computed: {
    isRegisterPage () {
      return this.$route?.name === 'register'
    },
    visibleAuthError () {
      return this.authError || this.$auth.error
    },
    ...mapGetters({
      profile: 'auth/getProfile',
      isAuthReady: 'auth/isAuthReady',
      authError: 'auth/getAuthError',
      isAuthInitializing: 'auth/isAuthInitializing'
    }),

    ...mapState({
      formData: state => state.form
    }),

    showLoading () {
      if (this.visibleAuthError && !this.$auth.loading && !this.isAuthInitializing) return false
      if (this.$auth.loading) return true
      if (this.isAuthInitializing) return true
      if (this.$auth.isAuthenticated && !this.isAuthReady) return true
      if (this.$auth.isAuthenticated && this.formData.loadingData) return true
      return false
    },

    loadingText () {
      if (this.$auth.loading) return 'Loading...'
      if (this.isAuthInitializing) return 'Authenticating...'
      if (this.$auth.isAuthenticated && !this.isAuthReady) return 'Setting up...'
      return 'Loading app data...'
    },

    isReady () {
      if (this.visibleAuthError) return false
      if (this.$auth.loading) return false
      if (!this.$auth.isAuthenticated) return true
      return this.isAuthReady && !this.formData.loadingData
    }
  },
  watch: {
    authError: {
      handler (error) {
        if (error?.action === 'register' && !this.isRegisterPage) this.$router.replace('/register').catch(() => {})
      },
      immediate: true
    },
    isAuthReady: {
      handler (ready) {
        if (ready && this.$arc && !this.profile) {
          this.handleData()
        }
      },
      immediate: true
    }
  },
  mounted () {
    // Listen for token-swap event
    bus.$on('token-swap', this.onTokenSwap)

    // If both conditions are already met on mount, call handleData
    if (this.isAuthReady && this.$arc && !this.profile) {
      this.handleData()
    }
  },
  beforeDestroy () {
    bus.$off('token-swap', this.onTokenSwap)
  },
  methods: {
    signInWithGoogle () {
      return this.$auth.loginWithRedirect({ appState: { targetUrl: this.$auth.targetUrl || this.$route.fullPath } })
    },
    onTokenSwap () {
      // Wait a tick for $arc to be available
      this.$nextTick(() => {
        if (this.$arc && this.isAuthReady && !this.profile) {
          this.handleData()
        }
      })
    },

    async handleData () {
      const client = this.$arc
      if (!client || !this.isAuthReady || this.dataLoadingClient === client) return
      this.dataLoadingClient = client
      try {
        await Promise.all([
          this.$store.dispatch('form/setData'),
          this.handleCurrentUser(client)
        ])
      } catch (error) {
        console.warn('ARC account data could not be loaded.')
      } finally {
        if (this.dataLoadingClient === client) this.dataLoadingClient = null
      }
    },

    async handleCurrentUser (client = this.$arc) {
      try {
        const { data: user } = await client.get('users/me')
        if (this.$arc !== client || !this.isAuthReady) return
        this.$store.dispatch('auth/setProfile', user)

        if (user && user.profile_picture_url !== null) {
          this.$store.dispatch('auth/setProfilePicture', user.profile_picture_url)
        }
      } catch (err) {
        console.warn('ARC user profile could not be loaded.')
      }
    },

    async retryAuth () {
      if (this.$auth.error) return this.signInWithGoogle()
      try {
        await initializeAuth(Vue)
        if (this.$route.path === '/login') {
          await this.$router.replace(this.$auth.targetUrl || '/')
        }
      } catch (error) {
        // The initializer exposes a sanitized error and resets its readiness promise.
      }
    }
  }
}
</script>

<style lang="scss">
@import 'assets/scss/all';
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap');

#app {
  font-family: 'Open Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #212741;
  #app-loading {
    text-align: center;
    margin-top: 60px;
  }
  > p {
    text-align: center;
    margin-top: 60px;
    a {
      color: #0158E3;
      cursor: pointer;
    }
  }
  .auth-error {
    text-align: center;
    margin-top: 60px;
    padding: 20px;
    p {
      color: #dc3545;
      margin-bottom: 20px;
    }
    button {
      background-color: #0158E3;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      &:hover {
        background-color: #0147b8;
      }
    }
  }
}
#loop-companion-mv3 {
  display: none !important;
}
</style>
